'use client';

import { c as _c } from "react/compiler-runtime";
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import Link from 'next/link';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, type PropsWithChildren, useEffect } from 'react';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
import { cx } from '../../cx';
import { useCanScroll } from '../../hooks/use-can-scroll';
import { Button } from '../button/button';
import { IconButton } from '../button/icon-button';
type CardSliderProps = PropsWithChildren<{
  fullBleed?: boolean;
}>;
export const CardSlider = forwardRef<HTMLDivElement, CardSliderProps>(function CardSlider(t0, ref) {
  const $ = _c(6);
  const {
    children,
    fullBleed
  } = t0;
  let t1;
  if ($[0] !== fullBleed) {
    t1 = cx("grid gap-6", {
      "full-bleed": fullBleed
    });
    $[0] = fullBleed;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== ref || $[4] !== t1) {
    t2 = <div ref={ref} className={t1}>{children}</div>;
    $[2] = children;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
});
export function CardSliderHeader(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex items-center justify-between">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function CardSliderTitle(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <h2 className="font-heading2">{children}</h2>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const cardSliderListVariants = tv({
  base: 'grid gap-4',
  variants: {
    isSlidable: {
      true: 'scrollbar-none max-w-full snap-x snap-mandatory scroll-px-6 list-none auto-cols-max grid-flow-col overflow-x-auto'
    },
    leftShadow: {
      true: 'shadow-slider-left'
    },
    rightShadow: {
      true: 'shadow-slider-right'
    }
  },
  compoundVariants: [{
    leftShadow: true,
    rightShadow: true,
    className: 'shadow-slider'
  }]
});
type ListProps = ComponentPropsWithoutRef<'ul'> & VariantProps<typeof cardSliderListVariants>;
export const CardSliderList = forwardRef<HTMLUListElement, ListProps>(function CardSliderList(t0, ref) {
  const $ = _c(17);
  let children;
  let className;
  let isSlidable;
  let leftShadow;
  let rest;
  let rightShadow;
  if ($[0] !== t0) {
    ({
      children,
      isSlidable,
      className,
      leftShadow,
      rightShadow,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isSlidable;
    $[4] = leftShadow;
    $[5] = rest;
    $[6] = rightShadow;
  } else {
    children = $[1];
    className = $[2];
    isSlidable = $[3];
    leftShadow = $[4];
    rest = $[5];
    rightShadow = $[6];
  }
  let t1;
  if ($[7] !== className || $[8] !== isSlidable || $[9] !== leftShadow || $[10] !== rightShadow) {
    t1 = cardSliderListVariants({
      isSlidable,
      className,
      leftShadow,
      rightShadow
    });
    $[7] = className;
    $[8] = isSlidable;
    $[9] = leftShadow;
    $[10] = rightShadow;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== children || $[13] !== ref || $[14] !== rest || $[15] !== t1) {
    t2 = <ul onTouchMove={_temp} onTouchStart={_temp2} ref={ref} className={t1} {...rest}>{children}</ul>;
    $[12] = children;
    $[13] = ref;
    $[14] = rest;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
});
type CardSliderListItemProps = PropsWithChildren<{
  snapStart?: boolean;
  className?: string;
}>;
export const CardSliderListItem = t0 => {
  const $ = _c(12);
  let children;
  let className;
  let rest;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      snapStart: t1,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
    t1 = $[4];
  }
  const snapStart = t1 === undefined ? true : t1;
  let t2;
  if ($[5] !== className || $[6] !== snapStart) {
    t2 = cx({
      "snap-start": snapStart
    }, className);
    $[5] = className;
    $[6] = snapStart;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== rest || $[10] !== t2) {
    t3 = <li className={t2} {...rest}>{children}</li>;
    $[8] = children;
    $[9] = rest;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export type SeeAllButtonProps = {
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
};
export function CardSliderSeeAllButton(t0) {
  const $ = _c(11);
  let children;
  let href;
  let onClick;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      href,
      onClick,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = href;
    $[3] = onClick;
    $[4] = rest;
  } else {
    children = $[1];
    href = $[2];
    onClick = $[3];
    rest = $[4];
  }
  const t1 = href ? Link : "button";
  let t2;
  if ($[5] !== children || $[6] !== href || $[7] !== onClick || $[8] !== rest || $[9] !== t1) {
    t2 = <Button {...rest} size="small" variant="secondary" as={t1} href={href} onClick={onClick} target="_blank">{children}</Button>;
    $[5] = children;
    $[6] = href;
    $[7] = onClick;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
type CardSliderNavButtonsProps = {
  sliderListRef: React.RefObject<HTMLUListElement | null>;
  amountOfItems: number;
  isLoading?: boolean;
  ariaLabelPrevious: string;
  ariaLabelNext: string;
};
export function CardSliderNavButtons(t0) {
  const $ = _c(23);
  const {
    sliderListRef,
    isLoading,
    ariaLabelPrevious,
    ariaLabelNext
  } = t0;
  const {
    canScrollLeft,
    canScrollRight
  } = useCanScroll(sliderListRef);
  let t1;
  if ($[0] !== sliderListRef.current) {
    t1 = direction => {
      const current = sliderListRef.current;
      if (!current) {
        return;
      }
      const itemWidth = current.firstElementChild?.clientWidth || 0;
      const scrollAmount = direction === "prev" ? -itemWidth : itemWidth;
      current.scrollBy({
        left: scrollAmount,
        behavior: "smooth"
      });
    };
    $[0] = sliderListRef.current;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleScroll = t1;
  let t2;
  if ($[2] !== isLoading || $[3] !== sliderListRef.current) {
    t2 = () => {
      if (isLoading) {
        const current_0 = sliderListRef.current;
        if (!current_0) {
          return;
        }
        current_0.scrollTo({
          left: 0,
          behavior: "instant"
        });
      }
    };
    $[2] = isLoading;
    $[3] = sliderListRef.current;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isLoading || $[6] !== sliderListRef) {
    t3 = [isLoading, sliderListRef];
    $[5] = isLoading;
    $[6] = sliderListRef;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== handleScroll) {
    t4 = () => handleScroll("prev");
    $[8] = handleScroll;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = !canScrollLeft;
  let t6;
  if ($[10] !== ariaLabelPrevious || $[11] !== t4 || $[12] !== t5) {
    t6 = <IconButton size="small" weight="solid" variant="blank" Icon={CaretLeft} onClick={t4} disabled={t5} aria-label={ariaLabelPrevious} />;
    $[10] = ariaLabelPrevious;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== handleScroll) {
    t7 = () => handleScroll("next");
    $[14] = handleScroll;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !canScrollRight;
  let t9;
  if ($[16] !== ariaLabelNext || $[17] !== t7 || $[18] !== t8) {
    t9 = <IconButton size="small" weight="solid" variant="blank" Icon={CaretRight} onClick={t7} disabled={t8} aria-label={ariaLabelNext} />;
    $[16] = ariaLabelNext;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t9) {
    t10 = <div className="flex gap-2">{t6}{t9}</div>;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp(e) {
  e.stopPropagation();
}
function _temp2(e_0) {
  e_0.stopPropagation();
}